import type { CorrelationRisk, RiskTerm } from '../../types';

/**
 * Which terms of a correlation risk "Redact all" redacts (sc-11465, Lake Stevens
 * R2-01/R2-02).
 *
 * Each term has a role: `redact` (it has to go to break the re-identification:
 * a name, a DOB, an ID) or `context` (the place, event or org that explains the
 * risk). Redact terms start selected and context terms don't; the reviewer can
 * drop any selected term ([X]) or add any other one before applying. A term the
 * Allow List keeps is never preselected. Risks stored before roles existed have
 * none: every term then starts selected, as it did.
 */

/** Per-risk overrides of the default selection, by term key. */
export type TermSelection = Record<string, boolean>;

export const termKey = (t: Pick<RiskTerm, 'text' | 'entity_type'>) => `${t.text}::${t.entity_type}`;

/** Whether the analysis gave the risk's terms roles (false for risks stored before). */
export function riskHasRoles(risk: CorrelationRisk): boolean {
  return risk.involved_terms.some((t) => t.role === 'redact' || t.role === 'context');
}

/** A term that only explains the risk (never true for a risk without roles). */
export function isContextTerm(term: RiskTerm, hasRoles: boolean): boolean {
  return hasRoles && term.role !== 'redact';
}

export function selectedByDefault(term: RiskTerm, hasRoles: boolean): boolean {
  if (term.allow_listed) return false;
  return !hasRoles || term.role === 'redact';
}

export function isTermSelected(term: RiskTerm, hasRoles: boolean, selection: TermSelection = {}): boolean {
  return selection[termKey(term)] ?? selectedByDefault(term, hasRoles);
}

/** The card's chip order: redact terms first, then context; otherwise as given. */
export function orderedTerms(risk: CorrelationRisk): RiskTerm[] {
  const hasRoles = riskHasRoles(risk);
  const seen = new Set<string>();
  const unique = risk.involved_terms.filter((t) => {
    const key = termKey(t);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return [
    ...unique.filter((t) => !isContextTerm(t, hasRoles)),
    ...unique.filter((t) => isContextTerm(t, hasRoles)),
  ];
}

const alreadyRedacted = (t: RiskTerm, pending: Record<string, boolean>) => !!t.in_dictionary || !!pending[termKey(t)];

/** What "Redact all (n)" adds to the dictionary: the selected terms not already in it. */
export function termsToRedact(
  risk: CorrelationRisk,
  selection: TermSelection = {},
  pending: Record<string, boolean> = {},
): RiskTerm[] {
  const hasRoles = riskHasRoles(risk);
  return orderedTerms(risk).filter((t) => isTermSelected(t, hasRoles, selection) && !alreadyRedacted(t, pending));
}

/**
 * The risk is handled when the dictionary covers every term it preselects (so it
 * stays handled after a reopen — FB-1007 #6). A risk that preselects nothing
 * counts as handled once any of its terms is redacted.
 */
export function riskCovered(risk: CorrelationRisk, pending: Record<string, boolean> = {}): boolean {
  const hasRoles = riskHasRoles(risk);
  const required = risk.involved_terms.filter((t) => selectedByDefault(t, hasRoles));
  if (required.length) return required.every((t) => alreadyRedacted(t, pending));
  return risk.involved_terms.some((t) => alreadyRedacted(t, pending));
}
